import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { fmt } from '../lib/formatters';

/**
 * Sección "Coste de entrada" de la ficha de producto: lo que de verdad ha
 * costado cada recepción (transporte y aranceles incluidos, en €), frente al
 * CDPe que se cotizó. Va FUERA del condicional de producto_proveedor_id: un
 * producto puede tener entradas sin tener ficha de proveedor enlazada.
 *
 * La desviación se mide contra cotizaciones.cdpe_eur de la cotización más
 * reciente ANTERIOR a la última entrada (la que se hizo para pedirla), con su
 * cambio USD/EUR al lado para que se vea cuánto es tipo de cambio y cuánto
 * es coste. Umbrales en parametros.coste_entrada_desviacion_umbrales.
 * Multi-cliente: las rutas se derivan de clienteCodigo.
 */

type Entrada = {
  cliente_id: string; producto_id: string | null; codigo_texto: string; codigo_norm: string; fecha: string; albaran: string | null;
  proveedor_id: string | null; proveedor_texto: string; proveedor_norm: string; proveedor_nombre: string | null;
  n_lineas: number; uds_total: number; uds_foc: number; importe_total: number; coste_entrada_ud: number | null;
  pedido_cliente_linea_id: string | null; enlace_manual: boolean; descripcion: string | null; resuelto_por: string;
  importacion_id: string | null; pedido_numero: string | null; pedido_id: string | null;
};
type Respuesta = {
  entradas: Entrada[];
  kpis: {
    ultimo_coste: number | null;
    ultima_entrada: { fecha: string; uds: number; uds_foc: number; proveedor: string } | null;
    comparable: { cotizacion_id: string; fecha: string; cdpe_eur: number | null; cambio_usd_eur: number | null } | null;
    desviacion_pct: number | null;
    n_entradas: number;
  };
  umbrales: { gris: number; ambar: number };
  cobertura_hasta: string | null;
};
type Candidato = {
  id: string; unidades: number; eta: string | null; fecha_entrega_real: string | null; precio_unitario: number | null;
  pedido_id: string; pedido_numero: string | null; estado: string; fecha_pedido: string; enlaces: number;
};

const ESTADO_PEDIDO: Record<string, string> = {
  PDTE_RESPUESTA: 'Pdte. respuesta', ACEPTADO: 'Aceptado', EN_PRODUCCION: 'En producción', EN_CAMINO: 'En camino',
  ENTREGADO: 'Entregado', INCIDENCIA: 'Incidencia', RECHAZADO: 'Rechazado', CANCELADO: 'Cancelado', FINALIZADO: 'Finalizado',
};

function colorDesviacion(d: number | null, u: { gris: number; ambar: number }): { cls: string; texto: string } {
  if (d == null) return { cls: 'text-slate-400', texto: 'sin comparable' };
  if (d <= -u.gris) return { cls: 'text-emerald-700', texto: 'por debajo del cotizado' };
  if (Math.abs(d) < u.gris) return { cls: 'text-slate-700', texto: 'en línea con lo cotizado' };
  if (d <= u.ambar) return { cls: 'text-amber-700', texto: 'por encima del cotizado' };
  return { cls: 'text-rose-700', texto: 'muy por encima del cotizado' };
}

export function CosteEntradaFicha({ productoId, clienteCodigo, clienteId }: { productoId: string; clienteCodigo: string; clienteId: string | null }) {
  const qc = useQueryClient();
  const [enlazando, setEnlazando] = useState<Entrada | null>(null);
  const base = `/${clienteCodigo.toLowerCase()}`;
  const q = useQuery({
    queryKey: ['coste-entrada', productoId],
    queryFn: () => api.get<Respuesta>(`/productos/${productoId}/coste-entrada`),
  });

  if (q.isLoading) return <Card title="Coste de entrada"><p className="text-sm text-slate-500">Cargando…</p></Card>;
  if (q.error) {
    return (
      <Card title="Coste de entrada">
        <p role="alert" className="text-sm text-rose-700">No se pudo cargar el coste de entrada.</p>
        <Button variant="secondary" size="sm" className="mt-2" onClick={() => q.refetch()}>Reintentar</Button>
      </Card>
    );
  }
  const d = q.data!;
  const k = d.kpis;
  const desv = colorDesviacion(k.desviacion_pct, d.umbrales);
  const variosCodigos = new Set(d.entradas.map((e) => e.codigo_norm)).size > 1;

  return (
    <Card
      title="Coste de entrada"
      description={`Coste real de recepción por unidad (transporte y aranceles incluidos), con las unidades gratis dentro de la media. Fuente: informe de coste de entrada de APPROX importado para ${clienteCodigo}.`}
      actions={<Link to={`${base}/importaciones`} className="text-xs text-slate-600 underline">Ficheros importados</Link>}
    >
      {k.n_entradas === 0 ? (
        <p className="text-sm text-slate-500">
          Sin coste de entrada: no hay entradas de este producto en los informes cargados
          {d.cobertura_hasta ? <> (cobertura hasta {fmt.date(d.cobertura_hasta)})</> : <> (ningún informe de coste de entrada importado para {clienteCodigo})</>}.
          {' '}Si el código del ERP es distinto, enlázalo en «Ficheros importados → Pendientes de enlazar».
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
            <Kpi label="Último coste de entrada" valor={k.ultimo_coste != null ? fmt.eur(k.ultimo_coste) : '—'} nota="por unidad, gratis incluidas" />
            <Kpi
              label="Última entrada"
              valor={k.ultima_entrada ? fmt.date(k.ultima_entrada.fecha) : '—'}
              nota={k.ultima_entrada ? `${fmt.int(k.ultima_entrada.uds)} uds${k.ultima_entrada.uds_foc ? ` (${fmt.int(k.ultima_entrada.uds_foc)} gratis)` : ''} · ${k.ultima_entrada.proveedor}` : undefined}
            />
            <Kpi
              label="Comparable cotizado (CDPe)"
              valor={k.comparable?.cdpe_eur != null ? fmt.eur(k.comparable.cdpe_eur) : '—'}
              nota={k.comparable ? `cotización del ${fmt.date(k.comparable.fecha)}${k.comparable.cambio_usd_eur != null ? ` · cambio ${fmt.number(k.comparable.cambio_usd_eur)}` : ''}` : 'sin cotización con CDPe'}
              href={k.comparable ? `${base}/cotizaciones/${k.comparable.cotizacion_id}` : undefined}
            />
            <Kpi
              label="Desviación última entrada"
              valor={k.desviacion_pct != null ? <span className={`${desv.cls} font-semibold`}>{k.desviacion_pct > 0 ? '+' : ''}{fmt.pct(k.desviacion_pct)}</span> : <span className="text-slate-400">—</span>}
              nota={desv.texto}
            />
          </div>

          <div className="mt-4 overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-xs">
              <thead className="bg-slate-50">
                <tr><Th>Fecha</Th><Th>Albarán</Th>{variosCodigos && <Th>Código ERP</Th>}<Th>Proveedor</Th><Th right>Uds</Th><Th right>Gratis</Th><Th right>Importe</Th><Th right>Coste/ud</Th><Th>Pedido {clienteCodigo}</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.entradas.map((e) => (
                  <tr key={`${e.fecha}|${e.albaran ?? ''}|${e.proveedor_norm}|${e.codigo_texto}`}>
                    <Td>{fmt.date(e.fecha)}</Td>
                    <Td>{e.albaran ?? <span className="text-slate-400">—</span>}</Td>
                    {variosCodigos && <Td><span className="font-mono">{e.codigo_texto}</span></Td>}
                    <Td>{e.proveedor_nombre ?? <span title="Sin proveedor enlazado en la app">{e.proveedor_texto}</span>}</Td>
                    <Td right>{fmt.int(e.uds_total)}</Td>
                    <Td right>{e.uds_foc ? fmt.int(e.uds_foc) : '—'}</Td>
                    <Td right>{fmt.eur(e.importe_total)}</Td>
                    <Td right><span className="font-semibold">{e.coste_entrada_ud != null ? fmt.eur(e.coste_entrada_ud) : '—'}</span></Td>
                    <Td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {e.pedido_cliente_linea_id && e.pedido_id
                          ? <Link to={`${base}/pedidos/${e.pedido_id}`} className="font-mono text-sky-700 underline">{e.pedido_numero ?? 'pedido'}</Link>
                          : e.enlace_manual
                            ? <Badge color="#94a3b8">Sin pedido (manual)</Badge>
                            : <span className="text-slate-400">sin enlazar</span>}
                        {e.enlace_manual && e.pedido_cliente_linea_id && <Badge color="#6366f1">Manual</Badge>}
                        {clienteId && <button type="button" onClick={() => setEnlazando(e)} className="cursor-pointer text-[11px] text-slate-600 underline">{e.pedido_cliente_linea_id ? 'cambiar' : 'enlazar'}</button>}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {enlazando && clienteId && (
        <PopoverEnlazar
          entrada={enlazando}
          productoId={productoId}
          clienteId={clienteId}
          clienteCodigo={clienteCodigo}
          onClose={() => setEnlazando(null)}
          onDone={() => { setEnlazando(null); qc.invalidateQueries({ queryKey: ['coste-entrada', productoId] }); }}
        />
      )}
    </Card>
  );
}

function PopoverEnlazar({ entrada, productoId, clienteId, clienteCodigo, onClose, onDone }: { entrada: Entrada; productoId: string; clienteId: string; clienteCodigo: string; onClose: () => void; onDone: () => void }) {
  const cand = useQuery({
    queryKey: ['candidatos-pedido', productoId, entrada.fecha, entrada.uds_total],
    queryFn: () => api.get<{ data: Candidato[]; sugerida: string | null; compatibles: string[] }>(`/coste-entrada/entrada/candidatos-pedido?producto_id=${productoId}&fecha=${entrada.fecha}&uds=${entrada.uds_total}`),
  });
  const [elegida, setElegida] = useState<string | null | undefined>(undefined);
  const patch = useMutation({
    mutationFn: (pedido_cliente_linea_id: string | null) => api.patch<{ ok: boolean }>('/coste-entrada/entrada/pedido-linea', {
      cliente_id: clienteId, codigo_norm: entrada.codigo_norm, fecha: entrada.fecha, albaran: entrada.albaran, proveedor_norm: entrada.proveedor_norm, pedido_cliente_linea_id,
    }),
    onSuccess: onDone,
  });
  const valor = elegida === undefined ? (entrada.pedido_cliente_linea_id ?? cand.data?.sugerida ?? null) : elegida;
  const bloqueado = patch.isPending || cand.isLoading || !!cand.error;

  return (
    <Modal open onClose={onClose} title={`Enlazar entrada del ${fmt.date(entrada.fecha)} (${fmt.int(entrada.uds_total)} uds)`} size="lg"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="save" disabled={bloqueado} onClick={() => patch.mutate(valor)}>{patch.isPending ? 'Guardando…' : valor ? 'Enlazar' : 'Dejar sin pedido'}</Button>
      </>}>
      {cand.isLoading && <p className="text-sm text-slate-500">Buscando líneas de pedido…</p>}
      {cand.error && <p role="alert" className="text-sm text-rose-700">No se pudieron cargar las líneas de pedido. Cierra y vuelve a intentarlo.</p>}
      {cand.data && (
        <div className="space-y-1 text-sm">
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-50">
            <input type="radio" name="linea" checked={valor === null} onChange={() => setElegida(null)} />
            <span className="text-slate-600">Sin pedido (esta entrada no corresponde a ninguna línea; no se volverá a enlazar sola)</span>
          </label>
          {cand.data.data.map((c) => {
            const compatible = cand.data!.compatibles.includes(c.id);
            return (
              <label key={c.id} className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 hover:bg-slate-50 ${compatible ? 'bg-emerald-50/60' : ''}`}>
                <input type="radio" name="linea" checked={valor === c.id} onChange={() => setElegida(c.id)} className="mt-1" />
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono">{c.pedido_numero ?? c.pedido_id.slice(0, 8)}</span>
                  <span>· {fmt.int(c.unidades)} uds{c.precio_unitario != null && <> · {fmt.eur(c.precio_unitario)}</>} · ETA {fmt.date(c.eta)}{c.fecha_entrega_real && <> · entregado {fmt.date(c.fecha_entrega_real)}</>}</span>
                  <Badge color="#94a3b8">{ESTADO_PEDIDO[c.estado] ?? c.estado}</Badge>
                  {compatible && <Badge color="#10b981">Compatible</Badge>}
                  {c.enlaces > 0 && c.id !== entrada.pedido_cliente_linea_id && <Badge color="#f59e0b">Ya enlazada a otra entrada</Badge>}
                </span>
              </label>
            );
          })}
          {cand.data.data.length === 0 && <p className="text-slate-500">Este producto no tiene líneas de pedido de {clienteCodigo} registradas.</p>}
        </div>
      )}
      {patch.error && <p role="alert" className="mt-2 text-xs text-rose-700">{(patch.error as Error).message}</p>}
    </Modal>
  );
}

function Kpi({ label, valor, nota, href }: { label: string; valor: React.ReactNode; nota?: string; href?: string }) {
  const cuerpo = (
    <>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-900">{valor}</div>
      {nota && <div className="text-[11px] text-slate-500">{nota}</div>}
    </>
  );
  return href
    ? <Link to={href} className="block rounded-md border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50">{cuerpo}</Link>
    : <div className="rounded-md border border-slate-200 bg-white px-3 py-2">{cuerpo}</div>;
}
function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <th scope="col" className={`px-3 py-2 font-semibold text-slate-600 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
}
function Td({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <td className={`px-3 py-2 align-top ${right ? 'text-right tabular-nums' : 'text-left'}`}>{children}</td>;
}
